// src/pages/GLBAPreferences.tsx

import React, { useEffect, useState } from 'react';
import { auth } from '../firebase/config';
import Navigation from '../components/Navigation';
import { getGLBAPreferences, updateGLBAPreferences } from '../lib/glba/glbaPreferencesApi';
import { GLBAPreferences as GLBAPreferencesType } from '../types/glbaPreferences';
import { Link } from 'react-router-dom';

const GLBAPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<GLBAPreferencesType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      const prefs = await getGLBAPreferences(user.uid);
      if (prefs) {
        setPreferences(prefs);
      } else {
        // Create default preferences if they don't exist
        const defaultPrefs: GLBAPreferencesType = {
          userId: user.uid,
          optOutInformationSharing: false,
          optOutAffiliateMarketing: false,
          updatedAt: new Date(),
        };
        setPreferences(defaultPrefs);
      }
    } catch (error: any) {
      console.error('Error loading preferences:', error);
      setStatus({ type: 'error', message: 'Failed to load preferences. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (field: 'optOutInformationSharing' | 'optOutAffiliateMarketing') => {
    if (!preferences) return;

    const user = auth.currentUser;
    if (!user) return;

    setIsSaving(true);
    setStatus(null);

    try {
      const newValue = !preferences[field];
      await updateGLBAPreferences(user.uid, {
        [field]: newValue,
      });

      setPreferences({
        ...preferences,
        [field]: newValue,
      });

      setStatus({
        type: 'success',
        message: `Your ${field === 'optOutInformationSharing' ? 'information sharing' : 'affiliate marketing'} opt-out preference has been updated.`,
      });
    } catch (error: any) {
      console.error('Error updating preferences:', error);
      setStatus({
        type: 'error',
        message: 'Failed to update preferences. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '1rem' }}>Financial Privacy Preferences</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Manage your privacy preferences under the Gramm-Leach-Bliley Act (GLBA).{' '}
          <Link to="/glba-privacy-notice" style={{ color: '#007bff', textDecoration: 'none' }}>
            View our Financial Privacy Notice
          </Link>
        </p>

        {status && (
          <div
            style={{
              padding: '1rem',
              marginBottom: '2rem',
              borderRadius: '4px',
              backgroundColor: status.type === 'success' ? '#d4edda' : '#f8d7da',
              border: `1px solid ${status.type === 'success' ? '#28a745' : '#dc3545'}`,
              color: status.type === 'success' ? '#155724' : '#721c24',
            }}
          >
            {status.message}
          </div>
        )}

        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Opt-Out Preferences</h2>
          <p style={{ marginBottom: '2rem', color: '#666' }}>
            Federal law gives you the right to limit some, but not all, sharing of your nonpublic 
            personal information. You can opt-out of the following:
          </p>

          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>
                  Opt-Out of Information Sharing with Nonaffiliated Third Parties
                </h3>
                <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
                  Opt-out of sharing your nonpublic personal information with nonaffiliated third 
                  parties for their marketing purposes. Note: You cannot opt-out of sharing that 
                  is necessary for us to provide our services (e.g., payment processors) or required by law.
                </p>
              </div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                marginLeft: '1rem'
              }}>
                <input
                  type="checkbox"
                  checked={preferences?.optOutInformationSharing ?? false}
                  onChange={() => handleToggle('optOutInformationSharing')}
                  disabled={isSaving}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: isSaving ? 'not-allowed' : 'pointer'
                  }}
                />
                <span style={{ marginLeft: '0.5rem', fontWeight: 'bold' }}>
                  {preferences?.optOutInformationSharing ? 'Opted Out' : 'Opt In'}
                </span>
              </label>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>
                  Opt-Out of Affiliate Marketing
                </h3>
                <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
                  Opt-out of sharing your information with our affiliates for their marketing purposes.
                </p>
              </div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                marginLeft: '1rem'
              }}>
                <input
                  type="checkbox"
                  checked={preferences?.optOutAffiliateMarketing ?? false}
                  onChange={() => handleToggle('optOutAffiliateMarketing')}
                  disabled={isSaving}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: isSaving ? 'not-allowed' : 'pointer'
                  }}
                />
                <span style={{ marginLeft: '0.5rem', fontWeight: 'bold' }}>
                  {preferences?.optOutAffiliateMarketing ? 'Opted Out' : 'Opt In'}
                </span>
              </label>
            </div>
          </div>

          <div style={{
            backgroundColor: '#e7f3ff',
            border: '1px solid #007bff',
            padding: '1rem',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}>
            <p style={{ margin: 0 }}>
              <strong>Processing Time:</strong> Your opt-out preferences will be processed within 30 days 
              of your request. You will receive email confirmation once your preferences have been updated.
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ marginTop: 0 }}>Important Information</h3>
          <ul style={{ marginBottom: 0 }}>
            <li>You can change your opt-out preferences at any time.</li>
            <li>Your preferences will remain in effect until you change them.</li>
            <li>Opting out does not affect sharing that is necessary for us to provide our services.</li>
            <li>Opting out does not affect sharing that is required by law.</li>
          </ul>
        </div>

        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '4px'
        }}>
          <h3 style={{ marginTop: 0 }}>Questions?</h3>
          <p style={{ marginBottom: 0 }}>
            If you have questions about your privacy preferences, please contact us at{' '}
            <a href="mailto:info@helpkeepmymoney.com" style={{ color: '#007bff' }}>
              info@helpkeepmymoney.com
            </a>{' '}
            or call (661) 524-6674.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GLBAPreferences;
